import { useCallback, useEffect, useRef, useState } from 'react';
import loaderWebm from '@lib/assets/images/loader-420.webm';
import loaderMp4 from '@lib/assets/images/loader-420.mp4';
import Logo from '@lib/assets/images/logo.svg?react';
import iOS from '@lib/utils/iOS';
import styles from './LogoLoader.module.scss';

const sources = [
  { src: loaderWebm, type: 'video/webm', key: 'webm' },
  { src: loaderMp4, type: 'video/mp4', key: 'mp4' },
];

const LOGO_TIMEOUT_MS = 2000;

export default function LogoLoader({
  setVisible,
}: {
  setVisible: (v: boolean) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);

  const isIOS = iOS();
  const showLogo = isIOS || autoplayBlocked;

  const hide = useCallback(() => setVisible(false), [setVisible]);

  useEffect(() => {
    if (isIOS) {
      const timeoutId = window.setTimeout(hide, LOGO_TIMEOUT_MS);
      return () => window.clearTimeout(timeoutId);
    }

    const video = videoRef.current;
    if (!video) {
      hide();
      return undefined;
    }

    let fallbackId: number | undefined;

    const playPromise = video.play();
    if (playPromise) {
      playPromise
        .then(() => {
          // Video is playing — safety fallback in case onEnded never fires
          const duration = video.duration || LOGO_TIMEOUT_MS / 1000;
          fallbackId = window.setTimeout(hide, (duration + 1) * 1000);
        })
        .catch(() => {
          // Autoplay blocked — switch to logo fallback
          setAutoplayBlocked(true);
          fallbackId = window.setTimeout(hide, LOGO_TIMEOUT_MS);
        });
    }

    return () => {
      if (fallbackId) window.clearTimeout(fallbackId);
    };
  }, [isIOS, hide]);

  return (
    <div className={styles.loaderWrap}>
      {showLogo ? (
        <Logo className={styles.loaderLogo} />
      ) : (
        <video
          className={styles.loaderVideo}
          controls={false}
          controlsList="nodownload nofullscreen noremoteplayback"
          disablePictureInPicture
          muted
          onEnded={hide}
          onError={hide}
          playsInline
          preload="auto"
          ref={videoRef}
        >
          {sources.map((source) => (
            <source key={source.key} src={source.src} type={source.type} />
          ))}
        </video>
      )}
    </div>
  );
}
